import { parseWorkDescription as parseSharedWorkDescription } from '../../shared/classroom/workContentParser';
export const WORK_CONTENT_VERSION = 2;

export type WorkQuestionType =
  | 'multiple_choice'
  | 'long_form'
  | 'short_answer'
  | 'true_false'
  | 'matching'
  | 'file_upload';

export type MatchPair = { left: string; right: string };

export type WorkQuestion = {
  id: string;
  type: WorkQuestionType;
  prompt: string;
  options: string[];
  answer: string;
  points: number;
  pairs?: MatchPair[];
};

export type AssignmentBuilderSettings = {
  shuffleQuestions: boolean;
  allowLateSubmission: boolean;
};

const defaultBuilderSettings: AssignmentBuilderSettings = {
  shuffleQuestions: false,
  allowLateSubmission: true,
};

export function newQuestionId(): string {
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultQuestion(type: WorkQuestionType): WorkQuestion {
  switch (type) {
    case 'long_form':
      return { id: newQuestionId(), type, prompt: '', options: [], answer: '', points: 1 };
    case 'short_answer':
      return { id: newQuestionId(), type, prompt: '', options: [], answer: '', points: 1 };
    case 'file_upload':
      return { id: newQuestionId(), type, prompt: '', options: [], answer: '', points: 1 };
    case 'true_false':
      return { id: newQuestionId(), type, prompt: '', options: ['True', 'False'], answer: 'True', points: 1 };
    case 'matching':
      return {
        id: newQuestionId(),
        type,
        prompt: '',
        options: [],
        answer: '',
        points: 1,
        pairs: [
          { left: '', right: '' },
          { left: '', right: '' },
        ],
      };
    default:
      return {
        id: newQuestionId(),
        type: 'multiple_choice',
        prompt: '',
        options: ['Option 1', 'Option 2'],
        answer: '',
        points: 1,
      };
  }
}

export function parseWorkDescription(raw: string | null | undefined): {
  instructions: string;
  questions: WorkQuestion[];
  settings: AssignmentBuilderSettings;
} {
  const parsed = parseSharedWorkDescription(raw);
  return {
    instructions: parsed.instructions,
    questions: parsed.questions as WorkQuestion[],
    settings: parsed.settings as AssignmentBuilderSettings,
  };
}

export function serializeWorkDescription(
  instructions: string,
  questions: WorkQuestion[],
  settings?: AssignmentBuilderSettings
): string {
  return JSON.stringify({
    version: WORK_CONTENT_VERSION,
    instructions: instructions.trim(),
    questions,
    builderSettings: settings ?? defaultBuilderSettings,
  });
}

export function questionTypeLabel(type: WorkQuestionType): string {
  switch (type) {
    case 'multiple_choice':
      return 'MULTIPLE CHOICE';
    case 'long_form':
      return 'LONG FORM';
    case 'short_answer':
      return 'SHORT ANSWER';
    case 'true_false':
      return 'TRUE / FALSE';
    case 'matching':
      return 'MATCHING';
    case 'file_upload':
      return 'FILE UPLOAD';
    default:
      return 'QUESTION';
  }
}
